import { Fragment, useState } from "react";
import { Icon } from "../components/Icon";
import { Card, PageHead, Seg, Toggle, Skeleton } from "../components/kit";
import { useCosts, useTurns, useMargin, useConfig, useRateCard, spendClass, fmtTokens, shortUser, fmtClockUTC } from "../lib/admin";

export function CostPage() {
  const cfg = useConfig();
  const [range, setRange] = useState("Today");
  const [openTurn, setOpenTurn] = useState<number | null>(null);
  // Live enforced knobs when signed in; local-only fallback otherwise.
  const [seedKill, setSeedKill] = useState(false);
  const [seedOpus, setSeedOpus] = useState(true);
  const kill = cfg ? cfg.knobs.killSwitch : seedKill;
  const opus = cfg ? cfg.knobs.opusEnabled : seedOpus;
  const globalCap = cfg ? cfg.knobs.globalDailyCapUsd : 2000;
  const orgCap = cfg ? cfg.knobs.orgDailyCapUsd : 120;
  const setKill = (v: boolean) => (cfg ? cfg.setKnob("kill_switch", v) : setSeedKill(v));
  const setOpus = (v: boolean) => (cfg ? cfg.setKnob("opus_enabled", v) : setSeedOpus(v));
  const editCap = (key: string, current: number) => {
    const next = Number(window.prompt("Set daily cap (USD)", String(current)));
    if (cfg && Number.isFinite(next) && next >= 0) cfg.setKnob(key, next);
  };

  // Live cross-org operator data when signed in; seed otherwise.
  const costs = useCosts();
  const turnsData = useTurns();
  const marginData = useMargin();
  const rates = useRateCard();

  const spend = costs
    ? costs.byModel.map((m) => ({ name: m.model || m.service, val: Math.round(m.costUsd * 100) / 100, cls: spendClass(m.service, m.model) }))
    : null;
  const total = costs ? Math.max(0.0001, costs.totalCostUsd) : 0;
  const blendedMargin = marginData?.blended.marginPct ?? null;
  const turnRows =
    turnsData
      ? turnsData.turns.map((t) => ({
          time: fmtClockUTC(t.ts),
          user: shortUser(t.userId),
          model: t.model.toLowerCase().includes("opus") ? "Opus" : "Sonnet",
          tok: fmtTokens((t.inputTokens || 0) + (t.outputTokens || 0)),
          cost: "$" + (t.cogsUsd ?? 0).toFixed(2),
          cr: t.creditsCharged,
          mode: t.mode === "coach" ? "Coach" : "Helper",
          userInput: t.userInput ?? "",
          assistantOutput: t.assistantOutput ?? "",
          hadScreenshot: !!t.hadScreenshot,
          tokensExact: ((t.inputTokens || 0) + (t.outputTokens || 0)).toLocaleString(),
          detail: true,
        }))
      : null;
  return (
    <div className="page">
      <PageHead title="Cost & Margin" sub="Where every dollar goes — and the live knobs to control it."
        actions={<Seg options={["Today", "7d", "30d"]} value={range} onChange={setRange} />} />

      <div className="bill-top">
        <Card title="Spend by provider" icon="receipt-text" iconColor="var(--g-green)" action={spend ? <span className="muted-sm">${total.toLocaleString()} today</span> : undefined}>
          {spend === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={16} />)}</div>
          ) : spend.length === 0 ? (
            <div className="gt-empty">No spend recorded yet today.</div>
          ) : (
            <>
              <div className="spend-list">
                {spend.map((s, i) => (
                  <div className="spend" key={i}>
                    <span className={`sp-dot ${s.cls}`} /><span className="sp-n">{s.name}</span>
                    <b className="sp-v">${s.val}</b>
                    <span className="sp-pct">{Math.round((s.val / total) * 100)}%</span>
                  </div>
                ))}
              </div>
              <div className="spend-bar">{spend.map((s, i) => <i key={i} className={s.cls} style={{ width: (s.val / total) * 100 + "%" }} />)}</div>
              <div className="margin-row"><span className="muted-sm">Blended margin</span><span className="margin-v">{blendedMargin != null ? `${blendedMargin}%` : "—"}</span></div>
            </>
          )}
        </Card>

        <Card title="Live knobs" icon="sliders-horizontal" iconColor="var(--g-amber)" pad={false}>
          <div className="set-list">
            <div className="set-row"><span className="set-ic"><Icon name="gauge" size={17} /></span><div className="set-body"><div className="set-t">Global daily cap</div><div className="set-d">Hard stop for all spend</div></div><div className="budget-val">${globalCap.toLocaleString()} <button className="icon-mini" onClick={() => editCap("global_daily_cap_usd", globalCap)}><Icon name="pencil" size={13} /></button></div></div>
            <div className="set-row"><span className="set-ic"><Icon name="building-2" size={17} /></span><div className="set-body"><div className="set-t">Org daily cap (default)</div><div className="set-d">Per-org ceiling</div></div><div className="budget-val">${orgCap.toLocaleString()} <button className="icon-mini" onClick={() => editCap("org_daily_cap_usd", orgCap)}><Icon name="pencil" size={13} /></button></div></div>
            <div className="set-row"><span className="set-ic"><Icon name="sparkles" size={17} /></span><div className="set-body"><div className="set-t">Opus enabled</div><div className="set-d">Allow the expensive model</div></div><Toggle on={opus} onChange={setOpus} /></div>
            <div className="set-row danger"><span className="set-ic crit"><Icon name="power" size={17} /></span><div className="set-body"><div className="set-t">Global kill switch</div><div className="set-d">Pause all inference now</div></div><Toggle on={kill} onChange={setKill} /></div>
          </div>
        </Card>
      </div>

      <div className="two-col">
        <Card title="Rate card (rates.ts)" icon="table" count="live" pad={false}>
          {rates === null ? (
            <div className="rate-list">{Array.from({ length: 7 }).map((_, i) => <div className="rate-row" key={i}><Skeleton h={14} /></div>)}</div>
          ) : (
            <div className="rate-list">
              {rates.rateCard.map((r, i) => (
                <div className="rate-row" key={i}><span className="rate-i">{r.item}</span><span className="rate-v">{r.rate}</span></div>
              ))}
              <div className="rate-row weights"><span className="rate-i">Credit weights</span><span className="rate-v">{rates.creditWeights.map((w) => `${w.model} ${w.weight}×`).join(" · ")}</span></div>
              <div className="rate-row weights"><span className="rate-i">Credit sale price</span><span className="rate-v">${rates.creditSalePriceUsd.toFixed(3)} / credit</span></div>
            </div>
          )}
        </Card>

        <Card title="Per-turn audit" icon="list" count="live" pad={false}>
          <div className="gtable turns-t">
            <div className="gt-head"><span>Time</span><span>User</span><span>Model</span><span>Tokens</span><span>Cost</span><span>Cr</span></div>
            {turnRows === null ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={18} />)}</div>
            ) : turnRows.length === 0 ? (
              <div className="gt-empty">No turns recorded yet.</div>
            ) : turnRows.map((t, i) => {
              const open = openTurn === i;
              return (
                <Fragment key={i}>
                  <div className={`gt-row ${t.detail ? "clickable" : "static"} ${open ? "open" : ""}`} onClick={() => t.detail && setOpenTurn(open ? null : i)}>
                    <span className="gt-mono">{t.time}</span>
                    <span className="gt-mut ellip">{t.user}</span>
                    <span><span className={`model-pill ${t.model.toLowerCase()}`}>{t.model}</span></span>
                    <span className="gt-mono">{t.tok}</span>
                    <span className="gt-mono">{t.cost}</span>
                    <span className="gt-mono">{t.cr}</span>
                  </div>
                  {open && (
                    <div className="turn-detail" style={{ gridColumn: "1 / -1" }}>
                      <div className="td-block"><div className="td-label">User input</div><div className="td-text">{t.userInput || "(no transcript stored)"}</div></div>
                      <div className="td-block"><div className="td-label">Assistant output</div><div className="td-text">{t.assistantOutput || "(no transcript stored)"}</div></div>
                      <div className="td-foot">
                        <span className={`level-tag ${t.mode === "Coach" ? "intermediate" : "core"}`}>{t.mode}</span>
                        <span className="td-chip"><Icon name="hash" size={11} />{t.tokensExact} tokens</span>
                        <span className="td-chip"><Icon name="zap" size={11} />{t.cr} cr · {t.cost}</span>
                        {t.hadScreenshot && <span className="td-chip"><Icon name="image" size={11} />screenshot</span>}
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
